import { GENERIC_ERROR_MESSAGE } from '@/constants/app';
import is from '@/utils/is';
import { useCallback, useEffect, useReducer, useRef } from 'react';

const REQUSET_STATES = {
  LOADING: 'LOADING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
};

type RequestState = {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  data: null | any;
  error: null | any;
};

type RequestAction = {
  type: string;
  data?: any;
  error?: any;
};

type UseTriggerRequestProps = {
  fetch: (...args: any[]) => Promise<any>;
  triggerOnMount?: boolean;
};

const intialRequestState: RequestState = {
  isLoading: false,
  isError: false,
  isSuccess: false,
  data: null,
  error: null,
};

const requestReducer = (state: RequestState, { type, data, error }: RequestAction) => {
  switch (type) {
    case REQUSET_STATES.LOADING:
      return { ...intialRequestState, isLoading: true };
    case REQUSET_STATES.SUCCESS:
      return {
        ...state,
        isLoading: false,
        isSuccess: true,
        data,
      };
    case REQUSET_STATES.ERROR:
      return {
        ...state,
        isLoading: false,
        isError: true,
        error,
      };
    case 'RESET':
      return intialRequestState;
    default:
      return state;
  }
};

const useTriggerRequest = ({ fetch, triggerOnMount = false }: UseTriggerRequestProps) => {
  const [{ isLoading, isError, isSuccess, data, error }, dispatch] = useReducer(
    requestReducer,
    intialRequestState,
  );

  const triggeredOnMount = useRef(false);

  const responseResolver = (response: any, resolve?: Function, reject?: Function) => {
    const { ok, status, data = {}, errorCode, ...rest } = response;
    let responseData = data;
    if (is.not.empty.object(rest)) {
      responseData = Object.assign(responseData, rest);
    }
    if (ok || status || responseData) {
      dispatch({ type: REQUSET_STATES.SUCCESS, data: responseData });
      if (resolve && is.function(resolve)) resolve(response);
    } else {
      dispatch({
        type: REQUSET_STATES.ERROR,
        error: new Error(`${GENERIC_ERROR_MESSAGE} | ${errorCode} | ${response.message}`),
      });
      if (reject && is.function(reject)) reject(response);
    }
  };

  const trigger = useCallback(
    (...args: any[]) => {
      const triggerFn = async () => {
        if (isLoading) return;
        dispatch({ type: REQUSET_STATES.LOADING });
        try {
          responseResolver(await fetch(...args));
        } catch (error: any) {
          let err = error?.data || error;
          console.error(err);
          err.statusCode = error?.statusCode;
          dispatch({
            type: REQUSET_STATES.ERROR,
            error: err,
          });
        }
      };
      triggerFn();
    },
    [isLoading, fetch],
  );

  const promisifyTrigger = useCallback(
    (...args: any[]): Promise<any> =>
      new Promise(async (resolve, reject) => {
        dispatch({ type: REQUSET_STATES.LOADING });
        if (isLoading) return;
        try {
          responseResolver(await fetch(...args), resolve, reject);
        } catch (error: any) {
          let err = error?.data || error;

          if (err?.statusCode) {
            err.statusCode = error?.statusCode;
          } else if (typeof err === 'object') {
            err['statusCode'] = error?.statusCode;
          } else {
            err = {
              statusCode: error?.statusCode,
              message: error?.data,
            };
          }
          console.error(err);
          dispatch({
            type: REQUSET_STATES.ERROR,
            error: err,
          });
          reject(err);
        }
      }),
    [isLoading, fetch],
  );

  useEffect(() => {
    if (triggerOnMount && !triggeredOnMount.current) {
      triggeredOnMount.current = true;
      trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = useCallback(() => {
    dispatch({
      type: 'RESET',
    });
  }, []);

  return {
    isLoading,
    isError,
    isSuccess,
    data,
    error,
    trigger,
    reset,
    promisifyTrigger,
  };
};

export default useTriggerRequest;
